// Angler progress: the part of a Junior Crew character that grows while a kid
// plays the games — fish caught, missions completed, experience and rank.
// Stored on the device instantly and synced to the crew locker (database).

import { readJSON, writeJSON } from "@/lib/junior-crew/storage";

export const ANGLER_PROGRESS_KEY = "jrcrew:angler-progress";
export const PROGRESS_EVENT = "jrcrew:progress";

export type AnglerProgress = {
  xp: number;
  catches: number;
  missions: number;
  species: string[];
};

export const EMPTY_PROGRESS: AnglerProgress = { xp: 0, catches: 0, missions: 0, species: [] };

/** Rank ladder — each rank needs a little more experience than the last. */
export const RANKS: { level: number; xp: number; name: string }[] = [
  { level: 1, xp: 0, name: "Deck Rookie" },
  { level: 2, xp: 60, name: "Bait Runner" },
  { level: 3, xp: 160, name: "Line Handler" },
  { level: 4, xp: 320, name: "Reel Ranger" },
  { level: 5, xp: 560, name: "Tide Tracker" },
  { level: 6, xp: 900, name: "Wave Rider" },
  { level: 7, xp: 1400, name: "Junior Captain" },
];

export function rankFor(xp: number) {
  let current = RANKS[0];
  for (const r of RANKS) if (xp >= r.xp) current = r;
  const next = RANKS.find((r) => r.xp > xp);
  const span = next ? next.xp - current.xp : 1;
  const into = next ? xp - current.xp : 1;
  return {
    level: current.level,
    name: current.name,
    next: next ?? null,
    progress: Math.max(0, Math.min(1, into / span)),
    toNext: next ? Math.max(0, next.xp - xp) : 0,
  };
}

export function readProgress(): AnglerProgress {
  const p = readJSON<AnglerProgress>(ANGLER_PROGRESS_KEY, EMPTY_PROGRESS);
  return {
    xp: Number(p?.xp) || 0,
    catches: Number(p?.catches) || 0,
    missions: Number(p?.missions) || 0,
    species: Array.isArray(p?.species) ? p.species.slice(0, 200) : [],
  };
}

export function writeProgress(p: AnglerProgress) {
  writeJSON(ANGLER_PROGRESS_KEY, p);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(PROGRESS_EVENT, { detail: p }));
  }
}

function bump(mutate: (p: AnglerProgress) => AnglerProgress): AnglerProgress {
  const next = mutate(readProgress());
  writeProgress(next);
  return next;
}

/** A landed fish: +10 XP, or +25 the first time that species is caught. */
export function recordCatch(species?: string): AnglerProgress {
  return bump((p) => {
    const id = (species ?? "").trim().toLowerCase();
    const isNew = id.length > 0 && !p.species.includes(id);
    return {
      xp: p.xp + (isNew ? 25 : 10),
      catches: p.catches + 1,
      missions: p.missions,
      species: isNew ? [...p.species, id].slice(0, 200) : p.species,
    };
  });
}

/** A finished mission, trip or game session: +20 XP. */
export function recordMission(): AnglerProgress {
  return bump((p) => ({ ...p, xp: p.xp + 20, missions: p.missions + 1 }));
}

/** Merge a database copy with whatever this device already has (highest wins). */
export function mergeProgress(a: AnglerProgress, b: AnglerProgress): AnglerProgress {
  return {
    xp: Math.max(a.xp, b.xp),
    catches: Math.max(a.catches, b.catches),
    missions: Math.max(a.missions, b.missions),
    species: Array.from(new Set([...a.species, ...b.species])).slice(0, 200),
  };
}
